'use client'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import { Calendar, FileText, MessageSquare, User } from 'react-feather'
import { useLocale, useTranslations } from '@/lib/next-shim'

import { StatusBadge } from '@/components/ui/StatusBadge'
import { GetMyInfosQuery } from '@/core/graphql/types'

type ExpertiseCardProps = {
  expertise: GetMyInfosQuery['myInfos']['expertises'][0]
  onClick: () => void
}

export function ExpertiseCard({ expertise, onClick }: ExpertiseCardProps) {
  const t = useTranslations()
  const lang = useLocale()

  const [formattedDate, setFormattedDate] = useState<string | null>(null)
  useEffect(() => {
    setFormattedDate(dayjs(expertise.startDate).locale(lang).format('D MMM YYYY'))
  }, [expertise.startDate, lang])

  const doctor = expertise.medecin?.userInfo
  const doctorName = doctor ? `Dr. ${doctor.name} ${doctor.surname}` : null
  const initials = doctor ? `${doctor.name?.[0] ?? ''}${doctor.surname?.[0] ?? ''}`.toUpperCase() : null
  const speciality = expertise.speciality || expertise.medecin?.speciality || t('account.expertises.noSpeciality')
  const statusText = t(`account.expertises.statuses.${expertise.status}`)

  const fileCount = expertise.files.length
  const messageCount = expertise.chatRoom?.messages?.length ?? 0

  return (
    <div
      onClick={onClick}
      className="group flex w-full cursor-pointer flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
    >
      <div className="flex items-center justify-between gap-2">
        <StatusBadge status={expertise.status} statusText={statusText} />
        <span className="flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-gray-400">
          <Calendar size={14} aria-hidden="true" />
          {formattedDate ?? ' '}
        </span>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold uppercase text-primary">
          {initials || <User size={20} aria-hidden="true" />}
        </span>
        <div className="min-w-0">
          <h3 className="truncate text-base font-bold capitalize text-gray-900">{speciality}</h3>
          {doctorName && <p className="truncate text-sm text-gray-500">{doctorName}</p>}
        </div>
      </div>

      <div className="mt-5 flex items-center gap-5 border-t border-gray-100 pt-3.5 text-sm text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <FileText size={15} aria-hidden="true" className="text-gray-400" />
          <span className="font-semibold tabular-nums text-gray-700">{fileCount}</span>
          {t('account.expertises.files')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <MessageSquare size={15} aria-hidden="true" className={messageCount > 0 ? 'text-primary' : 'text-gray-400'} />
          <span className={messageCount > 0 ? 'font-semibold tabular-nums text-primary' : 'font-semibold tabular-nums text-gray-700'}>{messageCount}</span>
          {t('account.expertises.messages')}
        </span>
      </div>
    </div>
  )
}
